import { Button } from '@components/common';
import { Outlet } from '@shared/types';
import { Lock, LockOpen, Pencil } from 'lucide-react';
import React from 'react';
import { AccountingPeriodRecord } from '../../../../types/accounting';

interface AccountingPeriodsTableProps {
  periods: AccountingPeriodRecord[];
  outlets: Outlet[];
  onEdit: (period: AccountingPeriodRecord) => void;
  onClosePeriod: (period: AccountingPeriodRecord) => void;
  onReopenPeriod: (period: AccountingPeriodRecord) => void;
}

function AccountingPeriodStatusBadge({
  status,
}: {
  status: AccountingPeriodRecord['status'];
}) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        status === 'open'
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {status === 'open' ? 'Open' : 'Closed'}
    </span>
  );
}

export function AccountingPeriodsTable({
  periods,
  outlets,
  onEdit,
  onClosePeriod,
  onReopenPeriod,
}: AccountingPeriodsTableProps) {
  const outletNameById = new Map(
    outlets.map((outlet) => [outlet.id, outlet.name]),
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Name
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Scope
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Range
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Notes
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {periods.map((period) => (
              <tr key={period.id} className="border-t border-gray-100">
                <td className="px-5 py-4 font-medium text-gray-900">
                  {period.name}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {period.outlet_id
                    ? outletNameById.get(period.outlet_id) || 'Outlet'
                    : 'Company'}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {period.period_start} - {period.period_end}
                </td>
                <td className="px-5 py-4">
                  <AccountingPeriodStatusBadge status={period.status} />
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {period.notes || 'No notes'}
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    {period.status === 'open' ? (
                      <>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => onEdit(period)}
                        >
                          <Pencil size={14} />
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => onClosePeriod(period)}
                        >
                          <Lock size={14} />
                          Close
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onReopenPeriod(period)}
                      >
                        <LockOpen size={14} />
                        Reopen
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
